import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, KanbanSquare, Users, Settings, Sun, LogOut, Menu, Package, SlidersHorizontal, Ruler, ScrollText, Mail, Send, CalendarClock, CalendarDays, Rocket, Cpu, HardHat, Percent, Network, FileSignature, Gauge, Building2, Contact, UserPlus, Images, Bot, Target, Gift, ClipboardCheck, FileStack, Wrench, Calculator, Gavel, Boxes, CreditCard, Wallet, Receipt, Archive } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS } from "../lib/constants";
import { cn } from "../lib/utils";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

const linkClass = ({ isActive }) =>
  cn(
    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
    isActive ? "bg-white/10 text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-white/5"
  );

const NavGroup = ({ color, label, testid, children, mt = "!mt-6" }) => (
  <div
    className={cn("relative rounded-xl border p-2 pt-3.5", mt)}
    style={{
      borderColor: `${color}59`,
      backgroundColor: `${color}17`,
    }}
    data-testid={testid}
  >
    <span
      className="absolute -top-2 left-3 px-1.5 bg-background text-[10px] uppercase tracking-[0.18em] font-bold"
      style={{ color }}
    >
      {label}
    </span>
    <div className="space-y-1.5">{children}</div>
  </div>
);

const PARAMETRI = [
  { to: "/immagini-azienda", label: "Immagini azienda", icon: Images, testid: "nav-immagini-azienda" },
  { to: "/email", label: "Email", icon: Mail, testid: "nav-email" },
  { to: "/automazioni-crm", label: "Automazioni CRM", icon: Bot, testid: "nav-automazioni-crm" },
  { to: "/qualita-crm", label: "Qualità CRM", icon: Gauge, testid: "nav-qualita" },
  { to: "/coda-email", label: "Coda email", icon: Send, testid: "nav-coda-email" },
];

const NAV_END = [
  { to: "/settings", label: "Impostazioni", icon: Settings, testid: "nav-settings" },
];

function Brand() {
  return (
    <div className="flex items-center gap-2">
      <img src="/torri-icon.png" alt="TORRI.SOLAR" className="h-9 w-9 rounded-full object-contain" />
      <div className="leading-tight">
        <div className="font-heading font-extrabold text-sm tracking-wide">TORRI<span style={{ color: "#FDD003" }}>.SOLAR</span></div>
        <div className="text-[10px] text-muted-foreground uppercase tracking-[0.15em]">Div. Chiavi in mano</div>
      </div>
    </div>
  );
}

function SidebarInner({ user, initials, onNavigate, onLogout }) {
  const isAdmin = user?.role === "admin" || user?.role === "partner";
  const isTL = user?.role === "team_leader";
  const canProspect = ["admin", "sales", "qualification", "partner", "team_leader"].includes(user?.role);
  const canGestionale = isAdmin || isTL || user?.role === "sales";
  return (
    <>
      <div className="h-16 flex items-center px-5 border-b border-border">
        <Brand />
      </div>
      <nav className="flex-1 p-3 space-y-1.5 overflow-y-auto">
        <NavGroup color="#FDD003" label="Lead" testid="nav-group-lead">
          <NavLink to="/" end data-testid="nav-dashboard" onClick={onNavigate} className={linkClass}>
            <LayoutDashboard className="h-4 w-4" strokeWidth={1.5} /> Dashboard
          </NavLink>
          <NavLink to="/pipeline" data-testid="nav-pipeline" onClick={onNavigate} className={linkClass}>
            <KanbanSquare className="h-4 w-4" strokeWidth={1.5} /> Pipeline
          </NavLink>
          <NavLink to="/leads" data-testid="nav-leads" onClick={onNavigate} className={linkClass}>
            <Users className="h-4 w-4" strokeWidth={1.5} /> Lead
          </NavLink>
        </NavGroup>

        <NavGroup color="#F97316" label="Area Tecnica" testid="nav-group-area-tecnica">
          <NavLink to="/progetti" data-testid="nav-progetti" onClick={onNavigate} className={linkClass}>
            <Rocket className="h-4 w-4" strokeWidth={1.5} /> Progetti
          </NavLink>
          <NavLink to="/tools" data-testid="nav-tools" onClick={onNavigate} className={linkClass}>
            <Wrench className="h-4 w-4" strokeWidth={1.5} /> Tools
          </NavLink>
          <NavLink to="/consulenze-tecniche" data-testid="nav-consulenze-tecniche" onClick={onNavigate} className={linkClass}>
            <ClipboardCheck className="h-4 w-4" strokeWidth={1.5} /> Consulenze tecniche
          </NavLink>
          {canGestionale && (
            <NavLink to="/preventivatore" data-testid="nav-preventivatore" onClick={onNavigate} className={linkClass}>
              <Calculator className="h-4 w-4" strokeWidth={1.5} /> Preventivatore
            </NavLink>
          )}
          {isAdmin && (
            <NavLink to="/manutenzioni" data-testid="nav-manutenzioni" onClick={onNavigate} className={linkClass}>
              <Wrench className="h-4 w-4" strokeWidth={1.5} /> Preventivi manutenzione
            </NavLink>
          )}
          {(user?.role === "qualification" || user?.role === "sales" || isTL) && (
            <NavLink to="/area-free" data-testid="nav-area-free-qualification" onClick={onNavigate} className={linkClass}>
              <Gift className="h-4 w-4" strokeWidth={1.5} /> Area Free
            </NavLink>
          )}
        </NavGroup>

        {(isAdmin || isTL) && (
          <div className="!mt-6 pb-1 px-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70 font-bold" data-testid="nav-section-sales">
            <Target className="h-3 w-3" strokeWidth={2} /> Sales
          </div>
        )}
        {canProspect && (
          <NavLink to="/sales/performance" data-testid="nav-sales-performance" onClick={onNavigate} className={linkClass}>
            <Target className="h-4 w-4" strokeWidth={1.5} /> Attività
          </NavLink>
        )}
        {isAdmin && (
          <NavLink to="/obiettivi-prospect" data-testid="nav-obiettivi-prospect" onClick={onNavigate} className={linkClass}>
            <Target className="h-4 w-4" strokeWidth={1.5} /> Obiettivi Sales
          </NavLink>
        )}
        {(isAdmin || isTL) && (
          <NavLink to="/appuntamenti" data-testid="nav-appuntamenti" onClick={onNavigate} className={linkClass}>
            <CalendarClock className="h-4 w-4" strokeWidth={1.5} /> Appuntamenti
          </NavLink>
        )}
        {isAdmin && (
          <NavLink to="/provvigioni" data-testid="nav-provvigioni" onClick={onNavigate} className={linkClass}>
            <Percent className="h-4 w-4" strokeWidth={1.5} /> Provvigioni
          </NavLink>
        )}
        {isTL && (<>
          <NavLink to="/qualita-crm" data-testid="nav-qualita-tl" onClick={onNavigate} className={linkClass}>
            <Gauge className="h-4 w-4" strokeWidth={1.5} /> Qualità CRM
          </NavLink>
          <NavLink to="/sopralluoghi" data-testid="nav-sopralluoghi-tl" onClick={onNavigate} className={linkClass}>
            <ClipboardCheck className="h-4 w-4" strokeWidth={1.5} /> Sopralluoghi
          </NavLink>
        </>)}
        {canProspect && (
          <NavLink to="/calendario" data-testid="nav-calendario" onClick={onNavigate} className={linkClass}>
            <CalendarDays className="h-4 w-4" strokeWidth={1.5} /> Calendario
          </NavLink>
        )}

        {canProspect && (
          <NavGroup color="#EF4444" label="Prospect" testid="nav-group-prospect">
            <NavLink to="/prospects/dashboard" data-testid="nav-prospect-dashboard" onClick={onNavigate} className={linkClass}>
              <LayoutDashboard className="h-4 w-4" strokeWidth={1.5} /> Dashboard
            </NavLink>
            <NavLink to="/prospects/pipeline" data-testid="nav-prospect-pipeline" onClick={onNavigate} className={linkClass}>
              <KanbanSquare className="h-4 w-4" strokeWidth={1.5} /> Pipeline
            </NavLink>
            <NavLink to="/prospects" data-testid="nav-prospect" onClick={onNavigate} className={linkClass}>
              <Building2 className="h-4 w-4" strokeWidth={1.5} /> Prospect
            </NavLink>
          </NavGroup>
        )}

        {isAdmin && (
          <NavGroup color="#38BDF8" label="Beyond Partner" testid="nav-group-beyond-partner">
            <NavLink to="/bacheca" data-testid="nav-bacheca" onClick={onNavigate} className={linkClass}>
              <HardHat className="h-4 w-4" strokeWidth={1.5} /> Bacheca per installatori
            </NavLink>
            <NavLink to="/installatori-partner" data-testid="nav-installatori-partner" onClick={onNavigate} className={linkClass}>
              <Network className="h-4 w-4" strokeWidth={1.5} /> Installatori Partner
            </NavLink>
            <NavLink to="/agenzie-abilitate" data-testid="nav-agenzie-abilitate" onClick={onNavigate} className={linkClass}>
              <Building2 className="h-4 w-4" strokeWidth={1.5} /> Agenzie Abilitate
            </NavLink>
            <NavLink to="/consulenze-rete" data-testid="nav-consulenze-rete" onClick={onNavigate} className={linkClass}>
              <FileStack className="h-4 w-4" strokeWidth={1.5} /> Consulenze Rete
            </NavLink>
            <NavLink to="/candidati-partner" data-testid="nav-candidati-partner" onClick={onNavigate} className={linkClass}>
              <UserPlus className="h-4 w-4" strokeWidth={1.5} /> Candidati Partner
            </NavLink>
            <NavLink to="/area-free" data-testid="nav-area-free" onClick={onNavigate} className={linkClass}>
              <Gift className="h-4 w-4" strokeWidth={1.5} /> Area Free
            </NavLink>
            <NavLink to="/products" data-testid="nav-products" onClick={onNavigate} className={linkClass}>
              <Package className="h-4 w-4" strokeWidth={1.5} /> Vetrina
            </NavLink>
          </NavGroup>
        )}

        {canGestionale && (
          <NavGroup color="#22C55E" label="Gestionale" testid="nav-group-gestionale">
            <NavLink to="/clienti" data-testid="nav-clienti" onClick={onNavigate} className={linkClass}>
              <Contact className="h-4 w-4" strokeWidth={1.5} /> Anagrafiche
            </NavLink>
            <NavLink to="/listino" data-testid="nav-listino" onClick={onNavigate} className={linkClass}>
              <ScrollText className="h-4 w-4" strokeWidth={1.5} /> Listino ufficiale
            </NavLink>
            <NavLink to="/costi-base" data-testid="nav-costi-base" onClick={onNavigate} className={linkClass}>
              <SlidersHorizontal className="h-4 w-4" strokeWidth={1.5} /> Listino costi base
            </NavLink>
            <NavLink to="/servizi" data-testid="nav-servizi" onClick={onNavigate} className={linkClass}>
              <Ruler className="h-4 w-4" strokeWidth={1.5} /> Listino servizi
            </NavLink>
            <NavLink to="/preventivi" data-testid="nav-preventivi" onClick={onNavigate} className={linkClass}>
              <Receipt className="h-4 w-4" strokeWidth={1.5} /> Preventivi
            </NavLink>
            <NavLink to="/ordini" data-testid="nav-ordini" onClick={onNavigate} className={linkClass}>
              <FileSignature className="h-4 w-4" strokeWidth={1.5} /> Ordini
            </NavLink>
            {isAdmin && (<>
            <NavLink to="/pagamenti" data-testid="nav-pagamenti" onClick={onNavigate} className={linkClass}>
              <CreditCard className="h-4 w-4" strokeWidth={1.5} /> Pagamenti
            </NavLink>
            <NavLink to="/bonus-fiscali" data-testid="nav-bonus-fiscali" onClick={onNavigate} className={linkClass}>
              <Percent className="h-4 w-4" strokeWidth={1.5} /> Bonus fiscali
            </NavLink>
            <NavLink to="/caparra" data-testid="nav-caparra" onClick={onNavigate} className={linkClass}>
              <Wallet className="h-4 w-4" strokeWidth={1.5} /> Caparra
            </NavLink>
            <NavLink to="/manutenzione" data-testid="nav-manutenzione" onClick={onNavigate} className={linkClass}>
              <Wrench className="h-4 w-4" strokeWidth={1.5} /> Manutenzione
            </NavLink>
            </>)}
          </NavGroup>
        )}

        {isAdmin && (
          <NavGroup color="#A78BFA" label="Condizioni" testid="nav-group-condizioni">
            <NavLink to="/condizioni" data-testid="nav-condizioni" onClick={onNavigate} className={linkClass}>
              <ScrollText className="h-4 w-4" strokeWidth={1.5} /> Condizioni chiavi in mano
            </NavLink>
            <NavLink to="/condizioni-vendita" data-testid="nav-condizioni-vendita" onClick={onNavigate} className={linkClass}>
              <Boxes className="h-4 w-4" strokeWidth={1.5} /> Condizioni vendita materiale
            </NavLink>
            {user?.role === "admin" && (
              <NavLink to="/margini" data-testid="nav-margini" onClick={onNavigate} className={linkClass}>
                <Percent className="h-4 w-4" strokeWidth={1.5} /> Margini
              </NavLink>
            )}
            <NavLink to="/sopralluoghi" data-testid="nav-sopralluoghi" onClick={onNavigate} className={linkClass}>
              <ClipboardCheck className="h-4 w-4" strokeWidth={1.5} /> Sopralluoghi
            </NavLink>
            <NavLink to="/articoli-subappalto" data-testid="nav-articoli-subappalto" onClick={onNavigate} className={linkClass}>
              <FileSignature className="h-4 w-4" strokeWidth={1.5} /> Articoli di subappalto
            </NavLink>
            <NavLink to="/testi-legali" data-testid="nav-testi-legali" onClick={onNavigate} className={linkClass}>
              <Gavel className="h-4 w-4" strokeWidth={1.5} /> Testi legali
            </NavLink>
          </NavGroup>
        )}

        {isAdmin && (
          <div className="!mt-6 pb-1 px-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70 font-bold">
            <SlidersHorizontal className="h-3 w-3" strokeWidth={2} /> Parametri
          </div>
        )}
        {isAdmin && PARAMETRI.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2.5 rounded text-sm font-medium transition-colors",
                isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              )
            }
          >
            <item.icon className="h-4 w-4" strokeWidth={1.5} />
            {item.label}
          </NavLink>
        ))}

        {user?.role === "admin" && (
          <NavLink to="/motore-bolletta" data-testid="nav-motore-bolletta" onClick={onNavigate} className={({ isActive }) => cn("flex items-center gap-3 px-3 py-2.5 rounded text-sm font-medium transition-colors", isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-secondary")}>
            <Gauge className="h-4 w-4" strokeWidth={1.5} /> Motore bolletta
          </NavLink>
        )}
        {user?.role === "admin" && (
          <NavLink to="/archivio-bollette" data-testid="nav-archivio-bollette" onClick={onNavigate} className={({ isActive }) => cn("flex items-center gap-3 px-3 py-2.5 rounded text-sm font-medium transition-colors", isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-secondary")}>
            <Archive className="h-4 w-4" strokeWidth={1.5} /> Archivio bollette
          </NavLink>
        )}

        {isAdmin && NAV_END.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2.5 rounded text-sm font-medium transition-colors",
                isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              )
            }
          >
            <item.icon className="h-4 w-4" strokeWidth={1.5} />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border">
        <NavLink to="/profilo" onClick={onNavigate} data-testid="nav-profilo"
          className={({ isActive }) => cn("flex items-center gap-3 px-2 py-2 rounded transition-colors", isActive ? "bg-primary/10" : "hover:bg-secondary")}>
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-secondary text-xs">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</div>
            <div className="text-[10px] text-muted-foreground truncate">{ROLE_LABELS[user?.role] || user?.role}</div>
          </div>
        </NavLink>
        <Button variant="ghost" size="sm" onClick={onLogout} data-testid="logout-btn" className="w-full justify-start text-muted-foreground hover:text-foreground mt-1">
          <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} /> Esci
        </Button>
      </div>
    </>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-60 shrink-0 border-r border-border flex-col fixed h-screen bg-card z-20">
        <SidebarInner user={user} initials={initials} onLogout={handleLogout} />
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-60 min-w-0">
        {/* Mobile top bar */}
        <div className="lg:hidden sticky top-0 z-30 h-14 flex items-center justify-between px-4 border-b border-border bg-card">
          <Brand />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn" aria-label="Apri menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-64 flex flex-col bg-card">
              <SidebarInner user={user} initials={initials} onNavigate={() => setOpen(false)} onLogout={handleLogout} />
            </SheetContent>
          </Sheet>
        </div>
        {children}
      </div>
    </div>
  );
}
